// https://www.typescriptlang.org/docs/handbook/triple-slash-directives.html is a thing
/// <reference types="@types/segment-analytics" />

import posthog from "posthog-js";
import TagManager from "react-gtm-module";

// Constants to follow our object-action-properties naming convention
// https://humanloop.notion.site/Analytics-Naming-Convention-c3409da767c64a4497d6eb213e3a446e

export enum EventObject {
  // Preferably use the semantic group if available and fallback
  // to the literal objects (e.g. Button) if that has its challenges.
  Button = "Button",
  Page = "Page",
  Form = "Form",
  Video = "Video",

  // Programmatic App
  AggregateResults = "Aggregate Results",
  CodeEditor = "Code Editor",
  Debug = "Debug",
  Datapoint = "DataPoint",
  Filter = "Filter",
  Function = "Function",
  GroundTruth = "Ground Truth",
  Pane = "Pane",
  SpanPopover = "Span Popover",
  Template = "Template",
  Project = "Project",
}

export enum EventAction {
  Changed = "changed",
  Clicked = "clicked",
  Created = "created",
  Disabled = "disabled",
  Enabled = "enabled",
  Exported = "exported",
  Played = "played",
  Resized = "resized",
  Run = "run",
  Saved = "saved",
  Scrolled = "scrolled",
  Searched = "searched",
  Selected = "selected",
  Sorted = "sorted",
  Submitted = "submitted",
  Viewed = "viewed",
}
const APPLICATION_CONTEXT = "website";

// TypeScript is unaware of the analytics property on window. To fix this, we declare a global by extending window:
// NB: When telemetry is disabled, this will be undefined.
declare global {
  interface Window {
    analytics: SegmentAnalytics.AnalyticsJS;
    gtag: any;
  }
}

export const identify = (userId: string, traits: any) => {
  window?.analytics?.identify(userId, {
    application_context: APPLICATION_CONTEXT,
    ...traits,
  });
  // PostHog
  posthog.identify(userId, {
    application_context: APPLICATION_CONTEXT,
    ...traits,
  });
};

const page = (
  // Page call will include other helpful properties automatically like url, title, referrer.
  category?: string,
  name?: string,
  properties?: Record<string, any>
) => {
  window?.analytics?.page(category, name, {
    application_context: APPLICATION_CONTEXT,
    ...properties,
  });
  // <PostHog /> component should do this automatically.
  // Sending a capture event anyw
  posthog.capture(`${EventObject.Page} ${EventAction.Viewed}`, {
    ...properties,
    category,
    name,
  });
};

const track = (
  eventObject: EventObject,
  action: EventAction,
  properties?: Record<string, any>
) => {
  window?.analytics?.track(`${eventObject} ${action}`, {
    action,
    application_context: APPLICATION_CONTEXT,
    ...properties,
  });

  // PostHog
  posthog.capture(`${eventObject} ${action}`, properties);

  // TODO: Fire GA event here. GA events need to be named differently however.
  // // GA
  // window.gtag("event", `${eventObject} ${action}`, properties);
  const noTagsAffected = TagManager.dataLayer({
    dataLayer: {
      event: "website_analytics",
      trackedEvent: `${eventObject} ${action}`,
      ...properties,
    },
  });
};

const reset = () => {
  window?.analytics?.reset();
};

export const Analytics = {
  identify,
  page,
  track,
  reset,
};
